/**
 * Error Logging Service (Frontend)
 *
 * MANDATORY FEATURE: Centralized Error Logging
 * Automatically sends all frontend errors to backend
 * Created: 2025-10-27
 * Status: MANDATORY - Must be used in all components
 */

import axios from "axios";
import {
  BACKGROUND_AUTH_SILENT_POLICY,
  getAPIURL,
} from "@/lib/utils/apiClient";
import { getPublicRuntimeConfig } from "@/lib/config/publicRuntimeConfig";

const API_BASE_URL = getAPIURL();
const SKIPPED_ENDPOINTS = [
  "/api/v1/errors/log",
  "/api/v1/system/client-log",
  "/api/v1/notifications/unread-count",
  "/api/v1/push/vapid-key",
  "/api/v1/push/subscribe",
  "/api/v1/push/unsubscribe",
];
const SKIPPED_AUTH_CODES = new Set([
  "missing_access_cookie",
  "invalid_access_token",
  "expired_access_token",
  "missing_refresh_cookie",
  "invalid_refresh_token",
  "expired_refresh_token",
  "user_cache_missing",
  "csrf_validation_failed",
  "refresh_race_retry",
  "refresh_in_progress_retry",
]);

interface ErrorData {
  message: string;
  stack?: string;
  url?: string;
  componentStack?: string;
  userAgent?: string;
  metadata?: Record<string, unknown>;
}

function asRecord(value: unknown): Record<string, unknown> | null {
  return value !== null && typeof value === "object"
    ? (value as Record<string, unknown>)
    : null;
}

function shouldIgnoreAPIError(status: number | undefined, endpoint: string) {
  return (
    status === 401 ||
    status === 403 ||
    endpoint.includes("/api/v1/notifications/unread-count") ||
    endpoint.includes("/api/v1/push/vapid-key") ||
    endpoint.includes("/api/v1/push/subscribe") ||
    endpoint.includes("/api/v1/push/unsubscribe")
  );
}

function metadataValue(
  metadata: Record<string, unknown> | undefined,
  key: string
): unknown {
  return metadata ? metadata[key] : undefined;
}

class ErrorLoggingService {
  private disabled = false;
  private disabledUntil = 0;
  private failureCount = 0;

  private isTemporarilyDisabled(): boolean {
    return this.disabled || Date.now() < this.disabledUntil;
  }

  private scheduleBackoff(): void {
    this.failureCount += 1;
    const backoffMs = Math.min(300000, 1000 * 2 ** this.failureCount);
    this.disabledUntil = Date.now() + backoffMs;
  }

  private resetBackoff(): void {
    this.failureCount = 0;
    this.disabledUntil = 0;
  }

  private shouldSkipError(errorData: ErrorData): boolean {
    const message = String(errorData.message || "");
    const endpoint = String(
      metadataValue(errorData.metadata, "endpoint") || ""
    );
    const status = Number(metadataValue(errorData.metadata, "status") || 0);
    const authCode = String(metadataValue(errorData.metadata, "code") || "");

    if (this.isTemporarilyDisabled()) return true;
    if (SKIPPED_ENDPOINTS.some((value) => endpoint.includes(value)))
      return true;
    if (/Network(?: |)Error/u.test(message)) {
      return true;
    }
    if (status === 401 || status === 403) return true;
    if (SKIPPED_AUTH_CODES.has(authCode)) return true;
    return false;
  }

  /**
   * Log error to backend
   * @param error - Error object or string
   * @param metadata - Additional context
   */
  async logError(
    error: Error | string,
    metadata: Record<string, unknown> = {}
  ) {
    try {
      const errorData: ErrorData = {
        message: typeof error === "string" ? error : error.message,
        stack: typeof error === "object" ? error.stack : undefined,
        url: typeof window !== "undefined" ? window.location.href : undefined,
        userAgent:
          typeof navigator !== "undefined" ? navigator.userAgent : undefined,
        metadata: {
          ...metadata,
          timestamp: new Date().toISOString(),
          userLanguage:
            typeof navigator !== "undefined" ? navigator.language : undefined,
        },
      };

      // Send to backend (non-blocking, don't await)
      // Skip logging if this is a network error to prevent infinite loop
      if (this.shouldSkipError(errorData)) {
        return;
      }

      axios
        .post(`${API_BASE_URL}/api/v1/errors/log`, errorData, {
          withCredentials: true,
          timeout: 5000,
          headers: {
            "X-Skip-Error-Logging": "1",
          },
          ...BACKGROUND_AUTH_SILENT_POLICY,
        })
        .catch((caught: unknown) => {
          const status = axios.isAxiosError(caught)
            ? caught.response?.status
            : undefined;
          if (status === 401 || status === 403) {
            // Endpoint blocked -> backoff to prevent loops.
            this.scheduleBackoff();
            return;
          }
          this.scheduleBackoff();
          // Silently fail to avoid client-side recursion.
        });
      this.resetBackoff();

      if (process.env.NODE_ENV === "development") {
        globalThis.console.debug("[IKAI CLIENT FAILURE]", errorData);
      }
    } catch {
      // Silent fallback to avoid recursion
    }
  }

  /**
   * Log React component error
   * @param error - Error object
   * @param errorInfo - React error info
   */
  async logReactError(
    error: Error,
    errorInfo: { componentStack?: string | null }
  ) {
    await this.logError(error, {
      componentStack: errorInfo.componentStack || "",
      errorType: "REACT_COMPONENT_FAILURE",
    });
  }

  /**
   * Log API error
   * @param error - Axios error
   * @param endpoint - API endpoint
   */
  async logAPIError(error: unknown, endpoint: string) {
    const axiosError = axios.isAxiosError(error) ? error : null;
    const responseData = asRecord(axiosError?.response?.data);
    // Log to console so console logger can capture it
    const errorMessage =
      (typeof responseData?.message === "string" && responseData.message) ||
      (error instanceof Error && error.message) ||
      "API_FAILURE";
    const status = axiosError?.response?.status;
    const statusText = axiosError?.response?.statusText;
    const code = responseData?.code;

    if (shouldIgnoreAPIError(status, endpoint)) {
      return;
    }

    // Format error message for console
    const consoleMessage = `[API FAILURE] ${status} ${statusText}: ${errorMessage} - ${endpoint}`;

    // Log to console (console logger will capture this) in development only
    if (process.env.NODE_ENV === "development") {
      if (status && status >= 400) {
        globalThis.console.error(consoleMessage, {
          endpoint,
          method: axiosError?.config?.method,
          status,
          statusText,
          responseData,
          error,
        });
      } else {
        globalThis.console.warn(consoleMessage, error);
      }
    }

    // Also send to backend error logging service (but skip network errors)
    if (!endpoint?.includes("/errors/log")) {
      const normalizedError =
        error instanceof Error || typeof error === "string"
          ? error
          : new Error(String(error));
      await this.logError(normalizedError, {
        endpoint,
        status,
        statusText,
        code,
        responseData,
        errorType: "API_FAILURE",
      });
    }
  }

  /**
   * Log unhandled promise rejection
   * @param reason - Rejection reason
   */
  async logUnhandledRejection(reason: unknown) {
    await this.logError(
      reason instanceof Error ? reason : new Error(String(reason)),
      { errorType: "UNHANDLED_PROMISE_REJECTION" }
    );
  }
}

// Singleton instance
export const errorLoggingService = new ErrorLoggingService();

// Legacy global handlers are intentionally opt-in to avoid duplicate
// error recursion with SentinelLogger.
const enableLegacyGlobalErrorLogger =
  getPublicRuntimeConfig().features.legacyErrorLogger === true;
if (typeof window !== "undefined" && enableLegacyGlobalErrorLogger) {
  // Catch unhandled errors
  window.addEventListener("error", (event) => {
    errorLoggingService.logError(event.error || event.message, {
      errorType: "UNCAUGHT_FAILURE",
      filename: event.filename,
      lineno: event.lineno,
      colno: event.colno,
    });
  });

  // Catch unhandled promise rejections
  window.addEventListener("unhandledrejection", (event) => {
    errorLoggingService.logUnhandledRejection(event.reason);
  });
}
